import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getFavorites, getRecent, toggleFavorite, isFavorite, pushRecent } from "@/lib/prefs";
import { Search, Package, Layers, MapPin, LayoutGrid, Zap, Star, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PAGES = [
  { id: "page:dashboard", label: "Dashboard live", to: "/admin", kind: "page" },
  { id: "page:reports", label: "Report", to: "/admin/reports", kind: "page" },
  { id: "page:activity", label: "Cronologia", to: "/admin/activity", kind: "page" },
  { id: "page:menu", label: "Menu (contenuti)", to: "/admin/menu", kind: "page" },
  { id: "page:availability", label: "Disponibilità Rapida", to: "/admin/availability", kind: "page" },
  { id: "page:menus", label: "Menu multipli", to: "/admin/menus", kind: "page" },
  { id: "page:stations", label: "Postazioni & QR", to: "/admin/stations", kind: "page" },
  { id: "page:customers", label: "Clienti CRM", to: "/admin/customers", kind: "page" },
  { id: "page:users", label: "Utenti", to: "/admin/users", kind: "page" },
  { id: "page:audit", label: "Audit log", to: "/admin/audit", kind: "page" },
  { id: "page:system", label: "Stato sistema", to: "/admin/system", kind: "page" },
  { id: "page:settings", label: "Impostazioni", to: "/admin/settings", kind: "page" },
  { id: "page:integrations", label: "Integrazioni", to: "/admin/integrations", kind: "page" },
  { id: "page:preview", label: "Vedi menu cliente", to: "/preview-menu", kind: "page", target: "_blank" },
];

const ACTIONS = [
  { id: "action:new-product", label: "Nuovo prodotto", to: "/admin/menu?new=product", kind: "action", hint: "N" },
  { id: "action:new-category", label: "Nuova categoria", to: "/admin/menu?new=category", kind: "action" },
  { id: "action:bulk-avail", label: "Azioni massive disponibilità", to: "/admin/availability", kind: "action" },
  { id: "action:qr-pdf", label: "Esporta PDF QR (A4)", kind: "action", url: "/api/admin/stations/qr-sheet.pdf?type=all&only_active=false" },
  { id: "action:qr-labels", label: "Esporta Etichette 10x15", kind: "action", url: "/api/admin/stations/qr-labels.pdf?type=all&only_active=false" },
];

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault(); setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return { open, setOpen };
}

export default function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [stations, setStations] = useState([]);
  const [selIdx, setSelIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setSelIdx(0);
    // Fetch once when opened
    Promise.all([
      api.get("/admin/products"), api.get("/admin/categories"), api.get("/admin/stations"),
    ]).then(([p, c, s]) => { setProducts(p.data); setCats(c.data); setStations(s.data); }).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const items = useMemo(() => {
    const fav = getFavorites();
    const recent = getRecent();
    const list = [
      ...products.map((p) => ({ id: `prod:${p.id}`, label: p.name, sub: `Prodotto · ${p.available ? "Disponibile" : "Esaurito"}`, kind: "product", to: `/admin/menu?open=${p.id}`, icon: <Package className="w-4 h-4" />, meta: p })),
      ...cats.map((c) => ({ id: `cat:${c.id}`, label: c.name, sub: `Categoria · ${c.department}`, kind: "category", to: `/admin/menu?cat=${c.id}`, icon: <Layers className="w-4 h-4" />, meta: c })),
      ...stations.map((s) => ({ id: `st:${s.id}`, label: s.name, sub: `Postazione ${s.type}${s.active ? "" : " · inattiva"}`, kind: "station", to: `/admin/stations`, icon: <MapPin className="w-4 h-4" />, meta: s })),
      ...PAGES.map((p) => ({ ...p, icon: <LayoutGrid className="w-4 h-4" />, sub: "Pagina" })),
      ...ACTIONS.map((a) => ({ ...a, icon: <Zap className="w-4 h-4" />, sub: "Azione" })),
    ];
    const nq = norm(q);
    let filtered = list;
    if (nq) {
      const terms = nq.split(/\s+/).filter(Boolean);
      filtered = list.filter((it) => {
        const hay = norm(`${it.label} ${it.sub || ""}`);
        return terms.every((t) => hay.includes(t));
      });
    } else {
      // Show favorites + recents first when no query
      const favIds = new Set([
        ...(fav.products || []).map((id) => `prod:${id}`),
        ...(fav.categories || []).map((id) => `cat:${id}`),
        ...(fav.pages || []),
      ]);
      const favItems = list.filter((it) => favIds.has(it.id));
      const recentIds = new Set((recent || []).map((r) => r.id));
      const recentItems = list.filter((it) => recentIds.has(it.id) && !favIds.has(it.id));
      const rest = list.filter((it) => !favIds.has(it.id) && !recentIds.has(it.id));
      filtered = [
        ...favItems.map((it) => ({ ...it, section: "Preferiti" })),
        ...recentItems.map((it) => ({ ...it, section: "Recenti" })),
        ...rest.slice(0, 30),
      ];
    }
    return filtered.slice(0, 60);
  }, [q, products, cats, stations]);

  const exec = (it) => {
    if (!it) return;
    if (it.url) {
      window.open(it.url, "_blank");
    } else if (it.to) {
      if (it.target === "_blank") window.open(it.to, "_blank");
      else nav(it.to);
      if (it.kind === "product" || it.kind === "category") {
        pushRecent({ id: it.id, label: it.label, kind: it.kind, to: it.to });
      }
    }
    onClose();
  };

  const toggleFav = (e, it) => {
    e.stopPropagation();
    const kindMap = { product: "products", category: "categories", page: "pages" };
    const kind = kindMap[it.kind];
    if (!kind) return;
    const idOnly = it.kind === "page" ? it.id : it.id.split(":")[1];
    toggleFavorite(kind, idOnly);
    toast.success(`${isFavorite(kind, idOnly) ? "Aggiunto ai" : "Rimosso dai"} preferiti`);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSelIdx((i) => Math.min(i + 1, items.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelIdx((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); exec(items[selIdx]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, selIdx]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:pt-24 bg-black/50" onClick={onClose} data-testid="command-palette">
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-sand-300 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-sand-300 px-3 py-2 bg-sand-50">
          <Search className="w-4 h-4 text-ink-500" />
          <input ref={inputRef} data-testid="cmdk-input"
                 value={q} onChange={(e) => { setQ(e.target.value); setSelIdx(0); }}
                 placeholder="Cerca prodotti, categorie, postazioni, pagine, azioni…"
                 className="flex-1 bg-transparent outline-none text-sm py-1" />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-sand-300 bg-white">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-ink-500">Nessun risultato per "{q}"</div>}
          {items.map((it, idx) => {
            const showSection = it.section && (idx === 0 || items[idx - 1]?.section !== it.section);
            return (
              <React.Fragment key={it.id}>
                {showSection && <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-ink-500 font-bold">{it.section}</div>}
                <button
                  onClick={() => exec(it)}
                  onMouseEnter={() => setSelIdx(idx)}
                  data-testid={`cmdk-item-${it.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm border-b border-sand-100 ${idx === selIdx ? "bg-bronze-50" : ""} hover:bg-bronze-50`}>
                  <span className="text-ink-500 shrink-0">{it.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{it.label}</div>
                    {it.sub && <div className="text-[11px] text-ink-500 truncate">{it.sub}</div>}
                  </div>
                  {(it.kind === "product" || it.kind === "category" || it.kind === "page") && (
                    <button onClick={(e) => toggleFav(e, it)} data-testid={`cmdk-fav-${it.id}`}
                            className="p-1 rounded hover:bg-white">
                      <Star className={`w-4 h-4 ${(() => { const km = { product: "products", category: "categories", page: "pages" }[it.kind]; const idOnly = it.kind === "page" ? it.id : it.id.split(":")[1]; return isFavorite(km, idOnly) ? "fill-amber-400 text-amber-500" : "text-ink-500"; })()}`} />
                    </button>
                  )}
                  <ArrowRight className="w-3 h-3 text-ink-500 opacity-60" />
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="border-t border-sand-300 bg-sand-50 px-3 py-1.5 text-[11px] text-ink-500 flex items-center gap-3">
          <span><kbd className="px-1 rounded border border-sand-300 bg-white">↑</kbd> <kbd className="px-1 rounded border border-sand-300 bg-white">↓</kbd> naviga</span>
          <span><kbd className="px-1 rounded border border-sand-300 bg-white">↵</kbd> apri</span>
          <span className="ml-auto">Cmd/Ctrl+K per aprire</span>
        </div>
      </div>
    </div>
  );
}
